import { useCallback } from 'react';
import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { z } from 'zod';
import { useMutation } from '@apollo/client';
import { useAuth } from 'src/utils/auth';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from 'src/components/ui/Button';
import TextField from 'src/components/ui/TextField';
import { Heading, Text } from 'react-aria-components';
import { graphQLErrorNotification } from 'src/utils/notifications';
import LockIcon from 'src/assets/icons/lock-5.svg';
import ArrowRightIcon from 'src/assets/icons/arrow-right-1.svg';
import OTP_LOGIN_MUTATION from 'src/graphql/mutation/otp-login.graphql';

import type { ClientToken } from 'src/types/client';

const OtpSchema = z.object({
  otp: z.string().length(6),
});

type OtpSchemaType = z.infer<typeof OtpSchema>;

const OtpLoginPage = () => {
  const { email } = Route.useSearch();

  return (
    <div>
      <Heading level={2} className="text-surface-alt-90 text-4xl font-bold">
        Welcome!
      </Heading>
      <Text
        elementType="p"
        className="text-sm text-surface-alt-75 font-normal mt-2 mb-8"
      >
        Enter the one-time password from the email to access your account.
      </Text>
      <OtpLoginForm email={email} />
    </div>
  );
};

const OtpLoginForm = ({ email = '' }: { email?: string }) => {
  const auth = useAuth();
  const router = useRouter();

  const { handleSubmit, clearErrors, reset, control } = useForm<OtpSchemaType>({
    resolver: zodResolver(OtpSchema),
    defaultValues: {
      otp: '',
    },
  });

  const [otpLogin, { loading }] = useMutation(OTP_LOGIN_MUTATION, {
    onCompleted: async (data) => {
      auth.onLogin(data.otpLogin as ClientToken);
      await router.invalidate();
    },
    onError: graphQLErrorNotification,
  });

  const onSubmit = useCallback(
    async ({ otp = '' }) => {
      const input = { otp, email };

      clearErrors();
      await otpLogin({ variables: { input } });
      reset();
    },
    [email, clearErrors, otpLogin, reset]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="otp"
        render={({ field, fieldState: { invalid, error } }) => (
          <TextField
            {...field}
            Icon={LockIcon}
            label="One-Time Password"
            placeholder="XXXXXX"
            validationBehavior="aria"
            isInvalid={invalid}
            errorMessage={error?.message}
            isRequired
            autoComplete="one-time-code"
          />
        )}
      />
      <Button
        className="w-full justify-center mt-8 gap-2"
        type="submit"
        isDisabled={loading}
        loading={loading}
      >
        Continue
        <ArrowRightIcon className="w-3 h-3 text-primary-45" />
      </Button>
    </form>
  );
};

const OtpLoginSchema = z.object({
  email: z.string().email(),
});

export const Route = createFileRoute('/_auth/otp-login')({
  component: OtpLoginPage,
  validateSearch: (search) => OtpLoginSchema.parse(search),
  beforeLoad: async ({ search }) => {
    const { success } = OtpLoginSchema.safeParse(search);

    if (!success) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/login',
      });
    }
  },
});
